import { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { sqlReadRaw as sqlRead } from "../utils/sqlClient";
import EstCoverSheet from "./estimate/EstCoverSheet";
import BidPackage from "./estimate/BidPackage";
import Milestones from "./estimate/Milestones";
import BODChecklist from "./estimate/BODChecklist";
import ScopeRegister from "./estimate/ScopeRegister";
import RoomTakeoff from "./estimate/RoomTakeoff";
import ElectricalEstimate from "./estimate/electrical/ElectricalEstimate";
import DuctEstimator from "./DuctEstimator";
import Estimate from "./estimate/Estimate";
import ExecutionSchedule from "./estimate/ExecutionSchedule";
import Revisions from "./estimate/Revisions";
import FinancialCompliancePanel from "./estimate/FinancialCompliancePanel";
import QuoteLog from "./estimate/QuoteLog";
import AssumedEquipment from "./estimate/AssumedEquipment";
import { useNavHeight } from "../hooks/useNavHeight";

// Deliberate variant of utils/format.js fmtDate: returns null (not "—") on
// missing input — callers here render nothing rather than an em-dash.
function fmtDate(d) {
  if (!d) return null;
  return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function daysUntil(dateStr) {
  if (!dateStr) return null;
  return Math.ceil((new Date(dateStr) - new Date()) / 86400000);
}

const DELIVERY_COLORS = {
  "Design-Build":  { bg: "#ede9fe", color: "#5b21b6" },
  "Plan-and-Spec": { bg: "#e0f2fe", color: "#0369a1" },
  "Both":          { bg: "#f0fdf4", color: "#166534" },
};

// Future sections shown as locked placeholders
const NAV_SECTIONS = [
  { id: "cover-sheet",  icon: "📋", label: "Cover Sheet",       active: true },
  { id: "revisions",    icon: "📐", label: "Revisions",         active: true },
  { id: "bid-package",  icon: "📁", label: "Bid Package",       active: true },
  { id: "quote-log",    icon: "💬", label: "Quote Log",         active: true },  // P3-A: this project's quotes + bid coverage (read-only)
  { id: "scope-raci",   icon: "🗂️", label: "Scope & RACI",      active: true },
  { id: "milestones",   icon: "📅", label: "Milestones",        active: true },
  { id: "bod-checklist",icon: "✅", label: "BOD Checklist",     active: true },
  { id: "financial-compliance", icon: "🧾", label: "Financial Compliance", active: true },
  { id: "divider" },
  { id: "room-takeoff", icon: "📏", label: "Takeoff & Design",  active: true },
  // "Assumed Equipment" (P2-A, 2026-09-12): the project's canonical
  // ProcessEquipment + EquipmentConnections register — id kept for URL stability.
  { id: "process-equipment", icon: "🔧", label: "Assumed Equipment", active: true },
  // Still the same standalone, SharePoint-backed calculator underneath (see
  // DuctEstimator.jsx's own header comment) — not yet wired to this
  // project's actual rooms/revision data. Long-term direction (per direct
  // feedback) is to tie it into each estimate the way Electrical already
  // is; this is step one, giving it a real home in the per-project nav
  // instead of a standalone top-nav tool with no project context at all.
  { id: "duct-estimator", icon: "🌀", label: "Duct & HEPA",     active: true },
  { id: "electrical",   icon: "⚡", label: "Electrical",        active: true },
  { id: "estimate",     icon: "💰", label: "Estimate",          active: true },
  // P4-SCHED (2026-09-13): the revision's conceptual execution schedule — activities from
  // scope, durations from production standards under the work pattern, CPM, Gantt.
  { id: "schedule",     icon: "📆", label: "Schedule",          active: true },
  { id: "submittal",    icon: "📦", label: "Submittal Package", active: false },
];

export default function EstimateDetail() {
  const { projectId } = useParams();
  const navigate      = useNavigate();
  const [searchParams] = useSearchParams();
  const urlSection     = searchParams.get("section");
  // The Room Takeoff Layout Canvas always opens in its own dedicated window
  // (see RoomTakeoff.jsx's openCanvasWindow) — that window's own page load
  // carries ?view=canvas and needs none of this page's sidebar/breadcrumb
  // chrome, just RoomTakeoff full-bleed (App.jsx does the same for the
  // outer Nav bar). RoomTakeoff loads its own data independent of
  // project/bidPkg below, so this doesn't wait on that fetch.
  const canvasOnly = searchParams.get("view") === "canvas" && urlSection === "room-takeoff";
  const [project, setProject]   = useState(null);
  const [bidPkg, setBidPkg]     = useState(null);
  const [loading, setLoading]   = useState(true);
  const [activeSection, setActiveSection] = useState(
    NAV_SECTIONS.some(s => s.id === urlSection && s.active) ? urlSection : "cover-sheet"
  );
  const [initialSectionSet, setInitialSectionSet] = useState(!!urlSection);
  const navHeight = useNavHeight();

  useEffect(() => {
    setLoading(true);
    sqlRead(`EXEC sp_GetEstimateDetail @project_id=${projectId}`)
      .then(result => {
        const proj = result?.Table1 || result?.value || [];
        const bp   = result?.Table2 || [];
        setProject(proj[0] || null);
        setBidPkg(bp[0] || null);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [projectId]);

  // Remembers the project + tab an estimator was just on, so Nav.jsx can
  // offer a one-click way back after they wander off to Setup or the
  // Estimating Assistant — "Estimates" up top only ever goes to the
  // top-level folder browser, per direct feedback. A plain per-viewer
  // convenience (which project you last had open), not real data, so
  // localStorage rather than a DB round-trip; self-maintaining since every
  // project visited just overwrites the same key.
  useEffect(() => {
    if (!project) return;
    try {
      localStorage.setItem("cs-last-estimate", JSON.stringify({
        projectId, section: activeSection, name: project.ProjectName || "",
      }));
    } catch { /* private window / storage blocked — the shortcut just won't appear */ }
  }, [projectId, activeSection, project]);

  // First visit to a project with no revisions yet lands on the Revisions tab,
  // where the existing "Create Rev 0 →" empty state is effectively "Start New Estimate."
  // Estimates that already have revisions keep the normal Bid Package default ("Continue").
  useEffect(() => {
    if (initialSectionSet) return;
    sqlRead(`EXEC sp_GetEstimateRevisions @project_id=${projectId}`)
      .then(result => {
        const revs = result?.Table1 || result?.value || [];
        if (revs.length === 0) setActiveSection("revisions");
      })
      .catch(console.error)
      .finally(() => setInitialSectionSet(true));
  }, [projectId, initialSectionSet]);

  if (canvasOnly) return <RoomTakeoff projectId={+projectId} />;

  if (loading) return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: `calc(100vh - ${navHeight}px)`, color: "var(--text3)", fontSize: 13 }}>
      <div className="spinner spinner-dark" style={{ display: "inline-block", marginRight: 10 }} />Loading estimate…
    </div>
  );

  if (!project) return (
    <div style={{ padding: 40, textAlign: "center", color: "var(--text3)" }}>
      <div style={{ fontSize: 28, marginBottom: 10 }}>🔍</div>
      Estimate not found. <button onClick={() => navigate("/estimates")} style={{ color: "var(--cs-blue)", background: "none", border: "none", cursor: "pointer" }}>Back to Estimates</button>
    </div>
  );

  const days = daysUntil(bidPkg?.BidDueDate);
  const dc   = DELIVERY_COLORS[bidPkg?.DeliveryMethod] || {};
  const urgentColor = days !== null && days <= 5 ? "var(--red)" : days !== null && days <= 10 ? "#d97706" : "var(--text2)";

  return (
    <div style={{ display: "flex", height: `calc(100vh - ${navHeight}px)` }}>

      {/* Sidebar */}
      <div style={{
        width: 200, flexShrink: 0, background: "var(--surface)",
        borderRight: "1px solid var(--cs-border)", padding: "16px 0",
        display: "flex", flexDirection: "column", gap: 2,
        overflowY: "auto",
      }}>
        {/* Back link */}
        <button
          onClick={() => navigate("/estimates")}
          style={{
            display: "flex", alignItems: "center", gap: 6,
            padding: "6px 16px 10px", fontSize: 12, color: "var(--text3)",
            background: "none", border: "none", cursor: "pointer", textAlign: "left",
          }}
        >
          ← Estimates
        </button>

        {/* Project name in sidebar */}
        <div style={{
          padding: "8px 16px 12px", borderBottom: "1px solid var(--cs-border)",
          marginBottom: 6,
        }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text)", lineHeight: 1.3, marginBottom: 2 }}>
            {project.ProjectName}
          </div>
          {project.ClientName && (
            <div style={{ fontSize: 11, color: "var(--text3)" }}>{project.ClientName}</div>
          )}
        </div>

        {NAV_SECTIONS.map(item => {
          if (item.id === "divider") return (
            <div key="divider" style={{ height: 1, background: "var(--cs-border)", margin: "8px 16px" }} />
          );
          return (
            <button
              key={item.id}
              onClick={() => item.active && setActiveSection(item.id)}
              style={{
                display: "flex", alignItems: "center", gap: 10,
                padding: "8px 16px", fontSize: 13, fontWeight: 500,
                color: !item.active ? "var(--text3)" : activeSection === item.id ? "var(--cs-blue)" : "var(--text2)",
                background: activeSection === item.id ? "var(--cs-blue-lt)" : "none",
                border: "none",
                borderRight: activeSection === item.id ? "2px solid var(--cs-blue)" : "2px solid transparent",
                cursor: item.active ? "pointer" : "default",
                width: "100%", textAlign: "left",
                opacity: item.active ? 1 : 0.45,
              }}
            >
              <span style={{ fontSize: 15 }}>{item.icon}</span>
              {item.label}
              {!item.active && <span style={{ fontSize: 9, marginLeft: "auto", color: "var(--text3)", letterSpacing: "0.05em" }}>SOON</span>}
            </button>
          );
        })}
      </div>

      {/* Main content */}
      <div style={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column" }}>

        {/* Breadcrumb header */}
        <div style={{
          padding: "10px 24px",
          borderBottom: "1px solid var(--cs-border)",
          background: "var(--surface)",
          display: "flex", alignItems: "center", gap: 12,
          flexShrink: 0, flexWrap: "wrap",
        }}>
          <button
            onClick={() => navigate("/estimates")}
            style={{ fontSize: 12, color: "var(--text3)", background: "none", border: "none", cursor: "pointer", padding: 0 }}
          >
            ← Estimates
          </button>
          <span style={{ color: "var(--cs-border)", fontSize: 14 }}>/</span>
          <span style={{ fontSize: 13, fontWeight: 600, color: "var(--text)" }}>{project.ProjectName}</span>

          {/* Meta chips */}
          <div style={{ display: "flex", gap: 6, alignItems: "center", marginLeft: 4, flexWrap: "wrap" }}>
            {bidPkg?.DeliveryMethod && (
              <span style={{ fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 100, ...dc }}>
                {bidPkg.DeliveryMethod}
              </span>
            )}
            {project.AssignedEstimator && (
              <span style={{ fontSize: 11, color: "var(--text2)", padding: "2px 8px", borderRadius: 100, background: "#f3f4f6", fontWeight: 500 }}>
                {project.AssignedEstimator}
              </span>
            )}
            {bidPkg?.BidDueDate && (
              <span style={{ fontSize: 11, fontWeight: 600, color: urgentColor, padding: "2px 8px", borderRadius: 100, background: days <= 5 ? "#fee2e2" : days <= 10 ? "#fef3c7" : "#f3f4f6" }}>
                Bid due {fmtDate(bidPkg.BidDueDate)}
                {days !== null && ` · ${days < 0 ? `${Math.abs(days)}d ago` : days === 0 ? "today" : `${days}d`}`}
              </span>
            )}
          </div>
        </div>

        {/* Section content */}
        <div style={{ flex: 1, padding: 24 }}>
          {activeSection === "cover-sheet"   && <EstCoverSheet project={project} bidPkg={bidPkg} />}
          {activeSection === "bid-package"   && <BidPackage   projectId={+projectId} project={project} bidPkg={bidPkg} onBidPkgChange={setBidPkg} />}
          {activeSection === "quote-log"     && <QuoteLog     project={project} bidPkg={bidPkg} />}
          {activeSection === "milestones"    && <Milestones   projectId={+projectId} />}
          {activeSection === "bod-checklist" && <BODChecklist projectId={+projectId} />}
          {activeSection === "financial-compliance" && <FinancialCompliancePanel projectId={+projectId} />}
          {activeSection === "scope-raci"    && <ScopeRegister projectId={+projectId} />}
          {activeSection === "room-takeoff"  && <RoomTakeoff  projectId={+projectId} />}
          {activeSection === "process-equipment" && <AssumedEquipment projectId={+projectId} />}
          {activeSection === "duct-estimator" && <DuctEstimator projectId={+projectId} />}
          {activeSection === "electrical"    && <ElectricalEstimate projectId={+projectId} />}
          {activeSection === "estimate"      && <Estimate projectId={+projectId} projectName={project?.ProjectName || ""} />}
          {activeSection === "schedule"      && <ExecutionSchedule projectId={+projectId} />}
          {activeSection === "revisions"     && <Revisions    projectId={+projectId} />}
        </div>
      </div>
    </div>
  );
}
